import { API_BASE_URL } from './api.config';
import { getAccessToken, refreshAccessToken, validateAndRefreshTokens } from './auth';
import { ChatMessage } from './api.types';

export interface ChatSocketEvent {
  type: 'MESSAGE_CREATED' | 'CHAT_VIEWED';
  chatId: string;
  productId: string;
  senderId: string;
  recipientId: string;
  message?: ChatMessage;
  viewedStatus?: 'VIEWED' | 'NOT_VIEWED';
  lastMessageAt?: string;
  unreadCount?: number;
}

type ChatSocketListener = (event: ChatSocketEvent) => void;
type ChatSocketStatusListener = (connected: boolean) => void;

const STOMP_TERMINATOR = '\0';
const RECONNECT_DELAY_MS = 5000;
const CONNECT_TIMEOUT_MS = 10000;
const HEARTBEAT_INTERVAL_MS = 10000;
const SERVER_ACTIVITY_TIMEOUT_MS = 30000;

export class ChatSocketService {
  private socket: WebSocket | null = null;
  private listeners = new Set<ChatSocketListener>();
  private statusListeners = new Set<ChatSocketStatusListener>();
  private reconnectTimeout: number | null = null;
  private connectTimeout: number | null = null;
  private heartbeatInterval: number | null = null;
  private heartbeatWatchdog: number | null = null;
  private connectPromise: Promise<void> | null = null;
  private authenticationRecoveryPromise: Promise<void> | null = null;
  private reconnectRequiresRefresh = false;
  private connectionGeneration = 0;
  private lastServerActivityAt = 0;
  private shouldReconnect = false;
  private connected = false;

  connect(): Promise<void> {
    this.shouldReconnect = true;
    if (this.authenticationRecoveryPromise) {
      return this.authenticationRecoveryPromise;
    }
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return Promise.resolve();
    }

    if (!this.connectPromise) {
      const generation = this.connectionGeneration;
      this.connectPromise = this.openAuthenticatedSocket(generation)
        .finally(() => {
          this.connectPromise = null;
        });
    }

    return this.connectPromise;
  }

  private async openAuthenticatedSocket(generation: number): Promise<void> {
    const authenticated = await validateAndRefreshTokens();
    if (!authenticated || !this.shouldReconnect || generation !== this.connectionGeneration) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    const socket = new WebSocket(this.getSocketUrl());
    this.socket = socket;
    this.startConnectTimeout(socket);

    socket.onopen = () => {
      if (this.socket !== socket) {
        return;
      }
      this.sendFrame('CONNECT', {
        'accept-version': '1.2',
        host: window.location.host,
        'heart-beat': `${HEARTBEAT_INTERVAL_MS},${HEARTBEAT_INTERVAL_MS}`,
        Authorization: `Bearer ${token}`
      });
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) {
        return;
      }
      this.lastServerActivityAt = Date.now();
      if (typeof event.data !== 'string' || event.data === '\n') {
        return;
      }

      event.data
        .split(STOMP_TERMINATOR)
        .filter(Boolean)
        .forEach(frame => this.handleFrame(frame));
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) {
        return;
      }
      const completedStompHandshake = this.connected;
      this.clearConnectTimeout();
      this.clearHeartbeatTimers();
      this.setConnected(false);
      this.socket = null;
      const requiresRefresh = !completedStompHandshake
        || event.code === 1008
        || this.reconnectRequiresRefresh;
      this.reconnectRequiresRefresh = false;
      if (requiresRefresh) {
        this.recoverAuthenticationAndReconnect(generation);
      } else {
        this.scheduleReconnect();
      }
    };

    socket.onerror = () => {
      if (this.socket === socket) {
        socket.close();
      }
    };
  }

  disconnect() {
    this.shouldReconnect = false;
    this.connectionGeneration += 1;
    this.clearReconnect();
    this.clearConnectTimeout();
    this.clearHeartbeatTimers();
    if (this.socket && this.connected) {
      this.sendFrame('DISCONNECT');
    }
    this.socket?.close();
    this.socket = null;
    this.setConnected(false);
  }

  subscribe(listener: ChatSocketListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeStatus(listener: ChatSocketStatusListener) {
    this.statusListeners.add(listener);
    listener(this.connected);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  isConnected() {
    return this.connected;
  }

  private handleFrame(frame: string) {
    const normalizedFrame = frame.replace(/\r\n/g, '\n');
    const separatorIndex = normalizedFrame.indexOf('\n\n');
    const headerBlock = separatorIndex >= 0
      ? normalizedFrame.slice(0, separatorIndex)
      : normalizedFrame;
    const body = separatorIndex >= 0
      ? normalizedFrame.slice(separatorIndex + 2)
      : '';
    const [command] = headerBlock.split('\n');

    if (command === 'CONNECTED') {
      this.clearConnectTimeout();
      this.sendFrame('SUBSCRIBE', {
        id: 'chat-events',
        destination: '/user/queue/messages'
      });
      this.startHeartbeatTimers();
      this.setConnected(true);
      return;
    }

    if (command === 'ERROR') {
      console.error('Chat WebSocket STOMP error:', body || headerBlock);
      this.reconnectRequiresRefresh = true;
      this.socket?.close();
      return;
    }

    if (command !== 'MESSAGE' || !body) {
      return;
    }

    try {
      const event = JSON.parse(body) as ChatSocketEvent;
      this.listeners.forEach(listener => listener(event));
    } catch (error) {
      console.error('Failed to parse chat socket event:', error);
    }
  }

  private sendFrame(command: string, headers: Record<string, string> = {}, body: string = '') {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const headerLines = Object.entries(headers).map(([key, value]) => `${key}:${value}`);
    const commandAndHeaders = [command, ...headerLines].join('\n');
    this.socket.send(`${commandAndHeaders}\n\n${body}${STOMP_TERMINATOR}`);
  }

  private startConnectTimeout(socket: WebSocket) {
    this.clearConnectTimeout();
    this.connectTimeout = window.setTimeout(() => {
      this.connectTimeout = null;
      if (this.socket === socket && !this.connected) {
        console.error('Chat WebSocket did not complete the STOMP handshake in time.');
        socket.close();
      }
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout() {
    if (this.connectTimeout !== null) {
      window.clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  private startHeartbeatTimers() {
    this.clearHeartbeatTimers();
    this.lastServerActivityAt = Date.now();
    this.heartbeatInterval = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send('\n');
      }
    }, HEARTBEAT_INTERVAL_MS);
    this.heartbeatWatchdog = window.setInterval(() => {
      if (Date.now() - this.lastServerActivityAt > SERVER_ACTIVITY_TIMEOUT_MS) {
        console.warn('Chat WebSocket became unresponsive; reconnecting.');
        this.socket?.close();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private clearHeartbeatTimers() {
    if (this.heartbeatInterval !== null) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatWatchdog !== null) {
      window.clearInterval(this.heartbeatWatchdog);
      this.heartbeatWatchdog = null;
    }
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect || this.reconnectTimeout !== null) {
      return;
    }

    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectTimeout = null;
      void this.connect();
    }, RECONNECT_DELAY_MS);
  }

  private recoverAuthenticationAndReconnect(generation: number) {
    if (!this.shouldReconnect || this.authenticationRecoveryPromise) {
      return;
    }

    this.authenticationRecoveryPromise = refreshAccessToken()
      .then((refreshResult) => {
        if (!refreshResult) {
          this.shouldReconnect = false;
          this.clearReconnect();
          return;
        }
        if (this.shouldReconnect && generation === this.connectionGeneration) {
          this.scheduleReconnect();
        }
      })
      .finally(() => {
        this.authenticationRecoveryPromise = null;
      });
  }

  private clearReconnect() {
    if (this.reconnectTimeout !== null) {
      window.clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private setConnected(connected: boolean) {
    if (this.connected === connected) {
      return;
    }

    this.connected = connected;
    this.statusListeners.forEach(listener => listener(connected));
  }

  private getSocketUrl() {
    const apiUrl = new URL(API_BASE_URL, window.location.origin);
    apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${apiUrl.toString().replace(/\/$/, '')}/ws`;
  }
}

export const chatSocketService = new ChatSocketService();
